/**
 * Streams an SSE response from the assistant endpoint.
 *
 * Uses `fetch` + a ReadableStream rather than `EventSource` because EventSource
 * doesn't support POST or custom headers (we need X-Public-Key and a JSON body).
 *
 *   const { ok, status, error } = await streamChat({
 *     endpoint, apiKey, body,
 *     onEvent: (event, data) => { ... },
 *     signal,
 *   });
 */
export async function streamChat({ endpoint, apiKey, body, onEvent, signal }) {
    let response;
    try {
        response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                'X-Public-Key': apiKey,
            },
            body: JSON.stringify(body),
            signal,
        });
    } catch (e) {
        return { ok: false, status: 0, error: e };
    }

    if (!response.ok) {
        let errorBody = null;
        try {
            errorBody = await response.json();
        } catch (_) { /* not JSON */ }
        return { ok: false, status: response.status, error: errorBody };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let pendingEvent = 'message';
    let pendingData = '';

    function flush() {
        if (pendingData) {
            let parsed;
            try { parsed = JSON.parse(pendingData); } catch (_) { parsed = pendingData; }
            try { onEvent(pendingEvent, parsed); } catch (e) { console.error('[AIWidget] onEvent error', e); }
        }
        pendingEvent = 'message';
        pendingData = '';
    }

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE lines are separated by \n; events are delimited by \n\n.
        let lineEnd;
        while ((lineEnd = buffer.indexOf('\n')) !== -1) {
            const line = buffer.slice(0, lineEnd).replace(/\r$/, '');
            buffer = buffer.slice(lineEnd + 1);

            if (line === '') {
                flush();
                continue;
            }
            if (line.startsWith(':')) continue; // SSE comment
            const colon = line.indexOf(':');
            const field = colon === -1 ? line : line.slice(0, colon);
            const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');

            if (field === 'event') pendingEvent = value;
            else if (field === 'data') pendingData += pendingData ? '\n' + value : value;
        }
    }
    flush();

    return { ok: true, status: 200 };
}
